import React, { useRef, useMemo } from 'react';
import { getProductCardLabels } from '@productSearch/containers/SearchResultsContainer/utilities/getProductCardLabels';
import {
  formatImageSource,
  getAddToCardLabels,
  isRestricted,
} from '@productSearch/helpers';
import { definedTranslation } from '@productSearch/i18n';
import { isUnavailable } from '@productSearch/utils/helpers';
import { ProductCard } from 'components/Cards/ProductCard';
import { ProductCardActionType } from 'components/Cards/ProductCard/ProductCard.types';
import isEqual from 'lodash/isEqual';

import { sendAnalytics } from '../../api/events';
import { handleAddToBoards } from '../../helpers';
import {
  buildAnalyticsParamsToPDP,
  mapMaterialsForAnalytics,
  pushLearnMoreCtaClickDataLayer,
} from '../../helpers/analytics';
interface ssColor {
  color_name: string;
  entity_id: string | number;
  exact_color: string;
  image_url: string;
  manufacturer_sku: string;
  sku: string;
}

// TODO: describe types
export interface ResultCardProps extends Record<string, any> {}

const ResultCard = React.memo<ResultCardProps>((props) => {
  const {
    material: product,
    imageSize = 252,
    selectedProject,
    onProductClick,
    projects,
    isSelected,
    cartItemsIds = [],
    isColorway = false,
    isMiniPDPFinish = false,
    isMobile,
    isHoverAllowed,
    isOutlined,
    onContextMenuClick,
    onAddToCart,
    itemListId = 'mini_pdp',
    itemListName = 'Mini PDP',
    analyticsEventLocation,
    index,
    intl,
    loadingCartIds,
  } = props;

  const {
    name,
    entityId,
    manufacturerSku,
    blurhash,
    isInRealTimeStock,
    isNew,
    isJustAdded,
    colorwayId,
    isCollection,
    isDigital,
  } = product;

  const isInStock = isInRealTimeStock && !isUnavailable(product, entityId);
  const selectedSSFacetColor = useRef<ssColor | null>(null);
  const isInCart = cartItemsIds.includes(entityId);
  const itemId = Number(selectedSSFacetColor?.current?.entity_id) || entityId;
  const isProduct = product?.productFinishType?.includes('Product');
  const isFinishMaterial =
    product?.productFinishType?.includes('Finish') || isMiniPDPFinish;
  const isAddToCartLoading = loadingCartIds?.includes(String(itemId));
  const cardLabel = isColorway ? product?.title : name;
  const { cardLabels } = useMemo(() => {
    return getProductCardLabels(intl);
  }, [intl]);
  const conditionalLabels = {
    ...getAddToCardLabels(
      {
        isGeoRestricted: product.isGeoRestricted,
        isProjectRestricted: product.isProjectRestricted,
        isUnavailable: isUnavailable(product, entityId),
      },
      cardLabels,
    ),
    saveButtonLabel: cardLabels?.saveButtonLabel,
  };
  const finalLabels = { ...(cardLabels || {}), ...conditionalLabels };

  const internalLabels = {
    orderNow: intl?.formatMessage(definedTranslation.card.orderNow),
    sampleFinishesLabel: intl?.formatMessage(
      definedTranslation.card.sampleFinishesLabel,
    ),
  };

  const actionButtonText = isRestricted(product)
    ? conditionalLabels.addToCartLabel
    : internalLabels?.sampleFinishesLabel;

  const imageUrl = useMemo(() => {
    const thumbnail = product.thumbnailImageUrl;

    // image from API comes with get param ?width=X, we need to replace that
    // to adjust size to column size
    const defaultSizeImage = thumbnail?.split('?')?.[0];
    const finalImageSize = Math.max(600, imageSize);
    const finalUrl = defaultSizeImage
      ? formatImageSource(defaultSizeImage, finalImageSize, false)
      : null;
    return finalUrl?.src || null;
  }, [product, imageSize]);

  let materialUrl: string;
  const pdpTrackParams: string = buildAnalyticsParamsToPDP({
    product,
    itemListId,
    itemListName,
    index: index !== undefined ? index + 1 : undefined,
  });

  if (colorwayId != null) {
    materialUrl = `${product.url}?activeChild=${colorwayId}&${pdpTrackParams}`;
  } else if (selectedSSFacetColor?.current != null) {
    materialUrl = `${product.url}?activeChild=${selectedSSFacetColor?.current?.entity_id}&${pdpTrackParams}`;
  } else if (isFinishMaterial || !isCollection) {
    materialUrl = `${product.url}?activeChild=${entityId}&${pdpTrackParams}`;
  } else {
    materialUrl = `${product.url}?${pdpTrackParams}`;
  }

  const handleFavoritesClick = async () => {
    const currentItemId = product?.id != null ? product.id : product.entityId;
    handleAddToBoards(
      product,
      itemListId,
      itemListName,
      product.gridIndex,
      analyticsEventLocation,
    );

    const stringIds = [String(currentItemId)];
    const value = { value: 'minipdp' };
    await sendAnalytics({ id: stringIds, type: 'favorite', ...value });
  };

  const handleAddToBoxClick = () => {
    const currentProduct = {
      ...product,
      entityId: selectedSSFacetColor?.current?.entity_id || product.entityId,
    };
    onAddToCart?.(currentProduct, isFinishMaterial);
  };

  const handleRedirect = (id?: string) => {
    const { gaType } = mapMaterialsForAnalytics(
      [product],
      cardLabel,
      'digital_sampling',
      'digital sampling',
    );
    pushLearnMoreCtaClickDataLayer(gaType, selectedProject);

    const newUrl =
      id != null && colorwayId == null
        ? `${product.url}?activeChild=${id}`
        : materialUrl;
    window.location.href = newUrl;
    return false;
  };

  const handleProductClick = (id?: string) => {
    const newUrl =
      id != null && colorwayId == null
        ? `${product.url}?activeChild=${id}`
        : materialUrl;
    onProductClick?.({
      ...product,
      url: newUrl,
      manufacturerSku:
        selectedSSFacetColor?.current?.manufacturer_sku || manufacturerSku,
      thumbnailImageUrl: imageUrl || product.thumbnailImageUrl || '',
      entityId: id || itemId,
      isColorApplied: Boolean(selectedSSFacetColor?.current?.manufacturer_sku),
      index,
    });
  };

  const handleRightClick = () => {
    onContextMenuClick?.({ ...product, entityId: itemId });
  };

  const isNewLabel = isNew
    ? [{ label: 'New' }]
    : isJustAdded
    ? [{ label: 'Just Added' }]
    : [];
  const blurhashString = blurhash?.length >= 6 ? blurhash : null;
  const finalCardUrl = isProduct
    ? materialUrl?.split('?activeChild')?.[0]
    : materialUrl;
  const actionType: ProductCardActionType = isProduct
    ? 'customAction'
    : 'addToCart';

  return (
    <ProductCard
      showOnHoverOnlyOvernight
      isCompactDescription
      showProjectsDropdown
      showBag
      title={''}
      tooltipTitle={cardLabel}
      subtitle2={cardLabel}
      entityId={entityId}
      imageUrl={imageUrl!}
      projects={projects}
      productUrl={finalCardUrl}
      topInfoItems={isNewLabel}
      isOvernight={isInStock}
      isMobileDevice={isMobile}
      showAdvancedInfo
      showSelectOnHover={false}
      isHoverAllowed={isHoverAllowed}
      isOutlined={isOutlined}
      isSelected={isSelected}
      showHoverFeatures={false}
      afterLabel={undefined}
      blurhash={blurhashString}
      showContainedInfo
      showExtraInfo={false}
      actionType={actionType}
      actionButtonText={actionButtonText}
      onClickAddToFavorites={isMobile ? undefined : handleFavoritesClick}
      selectedProject={selectedProject}
      onAddToBag={handleAddToBoxClick}
      isAddToCartLoading={isAddToCartLoading}
      isAddedToCart={isInCart}
      onProductClick={handleProductClick}
      onActionButtonClick={isDigital ? handleRedirect : handleProductClick}
      onRightClick={handleRightClick}
      isDigital={isDigital}
      {...finalLabels}
    />
  );
}, isEqual);

ResultCard.displayName = 'ResultCard';

export { ResultCard };
